import React, { createContext, useContext, useReducer, useCallback, useState, useEffect, useRef } from 'react';
import backendService from '../services/backendService';
import { debugLayers } from '../utils/layerDebug';
import { isValidImage } from '../utils/imageValidation';

// Tipos de ações para o reducer
const ActionTypes = {
  
  // Ações de canvas
  CLEAR_CANVAS: 'CLEAR_CANVAS',
  SAVE_CANVAS_STATE: 'SAVE_CANVAS_STATE',
  
  // Ações de imagens
  ADD_IMAGE: 'ADD_IMAGE',
  REMOVE_IMAGE: 'REMOVE_IMAGE',
  REMOVE_ALL_IMAGES: 'REMOVE_ALL_IMAGES',
  UPDATE_IMAGE_TRANSFORM: 'UPDATE_IMAGE_TRANSFORM',
  SET_SELECTED_IMAGE: 'SET_SELECTED_IMAGE',
  UPDATE_IMAGE_OPACITY: 'UPDATE_IMAGE_OPACITY',
  UPDATE_IMAGE_BLEND_MODE: 'UPDATE_IMAGE_BLEND_MODE',
  REORDER_IMAGES: 'REORDER_IMAGES',
  UPDATE_IMAGE: 'UPDATE_IMAGE',
  
  // Ações de histórico
  UNDO: 'UNDO',
  REDO: 'REDO',
  SAVE_HISTORY_STATE: 'SAVE_HISTORY_STATE',
  
  // UI/Mobile Drawer
  SET_MOBILE_LAYER_DRAWER_OPEN: 'SET_MOBILE_LAYER_DRAWER_OPEN',
  SET_MOBILE_LAYER_DRAWER_HEIGHT: 'SET_MOBILE_LAYER_DRAWER_HEIGHT',
  
  // Loading states
  SET_BACKGROUND_REMOVAL_LOADING: 'SET_BACKGROUND_REMOVAL_LOADING',
};

// Estado inicial
const initialState = {
  
  // Estado do canvas
  canvasState: null,
  
  // Imagens e camadas
  uploadedImages: [],
  selectedImageId: null,
  
  // Histórico
  history: [],
  historyIndex: -1,
  maxHistorySize: 50,
  
  // UI/Mobile Drawer
  mobileLayerDrawerOpen: false,
  mobileLayerDrawerHeight: 0,
  
  // Loading states
  isBackgroundRemovalLoading: false,
};

// Função para criar um snapshot do estado
const createStateSnapshot = (state, canvasDataUrl = null) => ({
  timestamp: Date.now(),
  canvasState: canvasDataUrl || state.canvasState,
  uploadedImages: state.uploadedImages.map(img => ({
    ...img,
    image: img.image, // Referência da imagem
    transform: { ...img.transform }
  })),
  selectedImageId: state.selectedImageId,
});

// Função para clonar profundamente as imagens
const cloneImages = (images) => {
  return images.map(img => ({
    ...img,
    transform: { ...img.transform }
  }));
};

// Reducer principal
const appReducer = (state, action) => {
  switch (action.type) {
    
    // Ações de canvas
    case ActionTypes.CLEAR_CANVAS:
      return { ...state, canvasState: null };
    case ActionTypes.SAVE_CANVAS_STATE:
      return { ...state, canvasState: action.payload };
    
    // Ações de imagens
    case ActionTypes.ADD_IMAGE:
      return {
        ...state,
        uploadedImages: [...state.uploadedImages, action.payload],
        selectedImageId: action.payload.id,
      };
    case ActionTypes.REMOVE_IMAGE:
      const filteredImages = state.uploadedImages.filter(img => img.id !== action.payload);
      return {
        ...state,
        uploadedImages: filteredImages,
        selectedImageId: filteredImages.length > 0 ? filteredImages[filteredImages.length - 1].id : null,
      };
    case ActionTypes.REMOVE_ALL_IMAGES:
      return {
        ...state,
        uploadedImages: [],
        selectedImageId: null,
      };
    case ActionTypes.UPDATE_IMAGE_TRANSFORM:
      return {
        ...state,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === action.payload.id
            ? { ...img, transform: action.payload.transform }
            : img
        ),
      };
    case ActionTypes.SET_SELECTED_IMAGE:
      return { ...state, selectedImageId: action.payload };
    case ActionTypes.UPDATE_IMAGE_OPACITY:
      return {
        ...state,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === action.payload.imageId
            ? { ...img, opacity: action.payload.opacity }
            : img
        ),
      };
    case ActionTypes.UPDATE_IMAGE_BLEND_MODE:
      return {
        ...state,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === action.payload.imageId
            ? { ...img, blendMode: action.payload.blendMode }
            : img
        ),
      };
    case ActionTypes.REORDER_IMAGES:
      console.log('[AppContext] REORDER_IMAGES - Nova ordem aplicada');
      return {
        ...state,
        uploadedImages: action.payload,
      };
    case ActionTypes.UPDATE_IMAGE:
      return {
        ...state,
        uploadedImages: state.uploadedImages.map(img =>
          img.id === action.payload.id
            ? { ...img, ...action.payload.updates }
            : img
        ),
      };
    
    // Ações de histórico
    case ActionTypes.SAVE_HISTORY_STATE:
      const newHistory = [...state.history.slice(0, state.historyIndex + 1)];
      newHistory.push(action.payload);
      
      // Limitar tamanho do histórico
      if (newHistory.length > state.maxHistorySize) {
        newHistory.shift();
      }
      
      return {
        ...state,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
    case ActionTypes.UNDO:
      if (state.historyIndex > 0) {
        const previousState = state.history[state.historyIndex - 1];
        return {
          ...state,
          historyIndex: state.historyIndex - 1,
          canvasState: previousState.canvasState,
          uploadedImages: cloneImages(previousState.uploadedImages),
          selectedImageId: previousState.selectedImageId,
        };
      }
      return state;
    case ActionTypes.REDO:
      if (state.historyIndex < state.history.length - 1) {
        const nextState = state.history[state.historyIndex + 1];
        return {
          ...state,
          historyIndex: state.historyIndex + 1,
          canvasState: nextState.canvasState,
          uploadedImages: cloneImages(nextState.uploadedImages),
          selectedImageId: nextState.selectedImageId,
        };
      }
      return state;
    
    // UI/Mobile Drawer
    case ActionTypes.SET_MOBILE_LAYER_DRAWER_OPEN:
      return { ...state, mobileLayerDrawerOpen: action.payload };
    case ActionTypes.SET_MOBILE_LAYER_DRAWER_HEIGHT:
      return { ...state, mobileLayerDrawerHeight: action.payload };
    
    case ActionTypes.SET_BACKGROUND_REMOVAL_LOADING:
      return { ...state, isBackgroundRemovalLoading: action.payload };
    
    default:
      return state;
  }
};

// Criar o contexto
const AppContext = createContext();

// Provider do contexto
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);


  // Funções de ação para canvas
  const clearCanvas = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_CANVAS });
  }, []);

  const saveCanvasState = useCallback((canvasState) => {
    dispatch({ type: ActionTypes.SAVE_CANVAS_STATE, payload: canvasState });
  }, []);

  // Funções de ação para imagens
  const addImage = useCallback((imageData) => {
    // Adicionar propriedades padrão se não existirem
    const imageWithDefaults = {
      ...imageData,
      opacity: imageData.opacity || 100,
      blendMode: imageData.blendMode || 'normal',
      isVisible: imageData.isVisible !== undefined ? imageData.isVisible : true,
      originalSrc: imageData.originalSrc || imageData.src, // Preserva imagem original
      // Garantir que transform tenha todas as propriedades necessárias
      transform: {
        ...imageData.transform,
        x: imageData.transform?.x ?? 0,
        y: imageData.transform?.y ?? 0,
        scale: imageData.transform?.scale ?? 1, // Escala padrão 1:1
        rotation: imageData.transform?.rotation ?? 0,
      }
    };
    dispatch({ type: ActionTypes.ADD_IMAGE, payload: imageWithDefaults });
  }, []);

  const removeImage = useCallback((imageId) => {
    dispatch({ type: ActionTypes.REMOVE_IMAGE, payload: imageId });
  }, []);

  const removeAllImages = useCallback(() => {
    dispatch({ type: ActionTypes.REMOVE_ALL_IMAGES });
  }, []);

  const updateImageTransform = useCallback((imageId, transform) => {
    // Garantir que transform tenha todas as propriedades necessárias
    const completeTransform = {
      x: transform.x ?? 0,
      y: transform.y ?? 0,
      scale: transform.scale ?? 1, // Escala padrão 1:1
      rotation: transform.rotation ?? 0,
      // Preservar width e height se existirem
      ...(transform.width !== undefined && { width: transform.width }),
      ...(transform.height !== undefined && { height: transform.height }),
    };

    dispatch({
      type: ActionTypes.UPDATE_IMAGE_TRANSFORM,
      payload: { id: imageId, transform: completeTransform }
    });
  }, []);

  const setSelectedImage = useCallback((imageId) => {
    dispatch({ type: ActionTypes.SET_SELECTED_IMAGE, payload: imageId });
  }, []);


  const updateImageOpacity = useCallback((imageId, opacity) => {
    dispatch({ type: ActionTypes.UPDATE_IMAGE_OPACITY, payload: { imageId, opacity } });
  }, []);

  const updateImageBlendMode = useCallback((imageId, blendMode) => {
    dispatch({ type: ActionTypes.UPDATE_IMAGE_BLEND_MODE, payload: { imageId, blendMode } });
  }, []);

  const reorderImages = useCallback((newOrder) => {
    dispatch({ type: ActionTypes.REORDER_IMAGES, payload: newOrder });
  }, []);

  const updateImage = useCallback((imageId, updates) => {
    const currentImage = state.uploadedImages.find(img => img.id === imageId);
    
    if (!currentImage) {
      console.warn(`⚠️ [updateImage] Imagem ${imageId} não encontrada para atualização`);
      return;
    }

    // ✅ SOLUÇÃO 7: Validar Image se está sendo atualizado
    if (updates.image) {
      // Validar se é instância válida
      if (!(updates.image instanceof HTMLImageElement)) {
        console.error('❌ [updateImage] Invalid Image object:', {
          imageId,
          imageType: typeof updates.image,
          imageConstructor: updates.image?.constructor?.name
        });
        return;
      }

      // Se image não está completo, aguardar
      if (!updates.image.complete) {
        console.warn('⚠️ [updateImage] Image não está completo, aguardando carregamento...');
        const img = updates.image;
        
        // Aguardar carregamento de forma assíncrona
        const loadPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Image load timeout'));
          }, 10000);

          const onLoad = () => {
            clearTimeout(timeout);
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            resolve(img);
          };

          const onError = () => {
            clearTimeout(timeout);
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            reject(new Error('Image load error'));
          };

          img.addEventListener('load', onLoad);
          img.addEventListener('error', onError);
        });

        // Aguardar e então atualizar
        loadPromise
          .then(() => {
            // Recursivamente chamar após carregar
            updateImage(imageId, updates);
          })
          .catch((error) => {
            console.error('❌ [updateImage] Image failed to load:', error);
          });
        return;
      }

      // Verificar dimensões
      if (!isValidImage(updates.image)) {
        console.warn('⚠️ [updateImage] Image não é válida (dimensões inválidas ou não completa)');
        // Não retornar, permitir atualização mas logar warning
      }
    }

    const preservedUpdates = {
      ...updates,
      // Preserve originalSrc if it exists and isn't being explicitly updated
      originalSrc: updates.originalSrc || currentImage?.originalSrc || currentImage?.src,
      // 🔧 NOVO: Adicionar timestamp para forçar re-render
      lastUpdate: Date.now()
    };

    console.log('📝 [updateImage] Atualizando imagem:', {
      imageId,
      hasNewImage: !!updates.image,
      imageComplete: updates.image?.complete,
      imageNaturalWidth: updates.image?.naturalWidth,
      isValid: updates.image ? isValidImage(updates.image) : 'N/A'
    });

    dispatch({
      type: ActionTypes.UPDATE_IMAGE,
      payload: { id: imageId, updates: preservedUpdates }
    });
  }, [state.uploadedImages]);


  // Função para duplicar uma imagem
  const duplicateImage = useCallback((imageId) => {
    const imageToDuplicate = state.uploadedImages.find(img => img.id === imageId);
    if (!imageToDuplicate) return;

    console.log('[AppContext] Iniciando duplicação de imagem:', imageId);

    // CRÍTICO: Criar nova instância do HTMLImageElement
    // Se usarmos a mesma referência, todas as duplicatas apontam para o mesmo objeto
    const newImg = new Image();
    newImg.crossOrigin = 'anonymous';

    // IMPORTANTE: Usar src da imagem (pode ser processada/editada)
    // NÃO usar originalSrc, pois queremos duplicar a imagem no estado atual
    const srcToUse = imageToDuplicate.src;

    newImg.onload = () => {
      const newImageData = {
        ...imageToDuplicate,
        id: `image-${Date.now()}-${Math.random()}`, // Novo ID único
        image: newImg, // NOVA instância de Image
        src: srcToUse,
        originalSrc: imageToDuplicate.originalSrc || srcToUse,
        name: (imageToDuplicate.name || 'Imagem') + ' (cópia)',
        transform: {
          ...imageToDuplicate.transform,
          x: imageToDuplicate.transform.x + 20, // Offset para não sobrepor
          y: imageToDuplicate.transform.y + 20
        }
      };

      console.log('[AppContext] Duplicata criada:', {
        originalId: imageId,
        newId: newImageData.id,
        newImageObject: newImg !== imageToDuplicate.image,
        srcLength: srcToUse.length
      });

      dispatch({ type: ActionTypes.ADD_IMAGE, payload: newImageData });
    };

    newImg.onerror = () => {
      console.error('[AppContext] Erro ao criar duplicata');
    };

    newImg.src = srcToUse;
  }, [state.uploadedImages]);

  // Funções de histórico
  const saveHistoryState = useCallback((canvasDataUrl = null) => {
    const snapshot = createStateSnapshot(state, canvasDataUrl);
    dispatch({ type: ActionTypes.SAVE_HISTORY_STATE, payload: snapshot });
  }, [state]);

  const undo = useCallback(() => {
    dispatch({ type: ActionTypes.UNDO });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: ActionTypes.REDO });
  }, []);

  // Função para aplicar transformações pré-definidas (fillHeight, fillWidth, fillAll)
  // DEVE estar DEPOIS de saveHistoryState para evitar erro de inicialização
  const applyImageTransform = useCallback((imageId, transformType) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }

    const { transform } = image;
    // Usar a primeira imagem como referência para dimensões do papel
    const firstImage = state.uploadedImages[0];
    if (!firstImage || !firstImage.image) {
      console.error('Nenhuma imagem disponível para referência');
      return;
    }
    const paperWidth = firstImage.image.width;
    const paperHeight = firstImage.image.height;

    if (!paperWidth || !paperHeight) {
      console.error('Dimensões da imagem não definidas');
      return;
    }

    // 🔧 IMPORTANTE: Usar dimensões ATUAIS da imagem (width/height do transform)
    // Não usar image.width ou image.height, pois podem estar desatualizados
    const currentWidth = transform.width;
    const currentHeight = transform.height;

    if (!currentWidth || !currentHeight) {
      console.error('Dimensões da imagem não definidas no transform');
      return;
    }

    console.log('📐 [applyImageTransform]', {
      type: transformType,
      currentWidth,
      currentHeight,
      paperWidth,
      paperHeight
    });

    let newTransform = { ...transform };
    const aspectRatio = currentWidth / currentHeight;

    switch (transformType) {
      case 'fillHeight':
        // Preencher altura total (centralizada horizontalmente)
        newTransform.height = paperHeight;
        newTransform.width = paperHeight * aspectRatio;
        newTransform.x = (paperWidth - newTransform.width) / 2;
        newTransform.y = 0;
        break;

      case 'fillWidth':
        // Preencher largura total (centralizada verticalmente)
        newTransform.width = paperWidth;
        newTransform.height = paperWidth / aspectRatio;
        newTransform.x = 0;
        newTransform.y = (paperHeight - newTransform.height) / 2;
        break;

      case 'fillAll':
        // Preencher todo o espaço
        const paperAspectRatio = paperWidth / paperHeight;

        if (aspectRatio > paperAspectRatio) {
          // Imagem é mais larga, preencher altura
          newTransform.height = paperHeight;
          newTransform.width = paperHeight * aspectRatio;
          newTransform.x = (paperWidth - newTransform.width) / 2;
          newTransform.y = 0;
        } else {
          // Imagem é mais alta, preencher largura
          newTransform.width = paperWidth;
          newTransform.height = paperWidth / aspectRatio;
          newTransform.x = 0;
          newTransform.y = (paperHeight - newTransform.height) / 2;
        }
        break;

      default:
        console.error('Tipo de transformação inválido:', transformType);
        return;
    }

    console.log('[applyImageTransform] Nova transformação:', newTransform);
    updateImageTransform(imageId, newTransform);
    saveHistoryState();
  }, [state.uploadedImages, updateImageTransform, saveHistoryState]);

  // UI/Mobile Drawer actions
  const setMobileLayerDrawerOpen = useCallback((open) => {
    dispatch({ type: ActionTypes.SET_MOBILE_LAYER_DRAWER_OPEN, payload: open });
  }, []);

  const setMobileLayerDrawerHeight = useCallback((height) => {
    dispatch({ type: ActionTypes.SET_MOBILE_LAYER_DRAWER_HEIGHT, payload: height });
  }, []);

  const setBackgroundRemovalLoading = useCallback((isLoading) => {
    dispatch({ type: ActionTypes.SET_BACKGROUND_REMOVAL_LOADING, payload: isLoading });
  }, []);

  // BACKEND INTEGRATION - Estados para backend e modal avançado
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [advancedEditingImage, setAdvancedEditingImage] = useState(null);
  const [advancedEditingMask, setAdvancedEditingMask] = useState(null);

  // 🗑️ Estado para modal de confirmação de exclusão
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  // EFEITOS - Estados para painel de efeitos
  const [isEffectsPanelOpen, setIsEffectsPanelOpen] = useState(false);
  const [effectsEditingImage, setEffectsEditingImage] = useState(null);
  
  // FILTROS DE COR - Estados para filtros de cor (NOVO SISTEMA COM LAZY LOADING)
  const [isColorFiltersPanelOpen, setIsColorFiltersPanelOpen] = useState(false);
  const [colorFilterEditingImage, setColorFilterEditingImage] = useState(null);
  const [activeColorFilterType, setActiveColorFilterType] = useState(null); // null = seleção, 'gradient', 'duotone', 'solid'
  const [colorFilterSettings, setColorFilterSettings] = useState({
    gradient: {
      colors: ['#FF6B6B', '#4ECDC4'],
      angle: 135,
      opacity: 50,
      blendMode: 'overlay'
    },
    duotone: {
      shadowColor: '#000000',
      highlightColor: '#ffffff',
      opacity: 100,
      blendMode: 'normal'
    },
    solid: {
      color: '#FF6B6B',
      opacity: 50,
      blendMode: 'overlay'
    }
  });
  const [getColorFilterCanvasCallback, setGetColorFilterCanvasCallback] = useState(null);
  const [colorFilterUpdateCounter, setColorFilterUpdateCounter] = useState(0); // Contador para forçar re-render
  
  // FILTROS DE COR LEGACY - Manter compatibilidade temporária
  const [selectedColorFilter, setSelectedColorFilter] = useState(null);
  const [colorBlendMode, setColorBlendMode] = useState('multiply');
  const [colorOpacity, setColorOpacity] = useState(50);
  const [activeColorFilterCanvas, setActiveColorFilterCanvas] = useState(null);
  
  // 📄 PAPEL - Estado para painel de configuração de papel
  const [isPaperPanelOpen, setIsPaperPanelOpen] = useState(false);
  
  // PINTURA - Estados para ferramenta de pintura
  const [isPaintingActive, setIsPaintingActive] = useState(false);
  const [isSimplePaintingMode, setIsSimplePaintingMode] = useState(false); // NOVO: Modo pintura simplificado
  const [isPainting2Mode, setIsPainting2Mode] = useState(false); // 🎨 NOVO: Modo Pintura2
  const [paintingSettings, setPaintingSettings] = useState({
    brushSize: 20,
    brushColor: '#000000',
    brushOpacity: 100,
    brushFlow: 6,
    brushSpacing: 0, // Mantido em 0 (removido da interface)
    brushAngle: 0, // Mantido em 0 (removido da interface)
    brushRotate: false, // Mantido desabilitado (removido da interface)
    currentTool: 'pen',
    stabilizerLevel: 0, // Mantido em 0 (removido da interface)
    stabilizerWeight: 0, // Mantido em 0 (removido da interface)
    currentBrushImage: null,
    blendMode: 'source-over' // Modo de mesclagem padrão
  });
  
  // ANÁLISE DE ESTADO - Estado para painel de análise
  const [isStateAnalyzerOpen, setIsStateAnalyzerOpen] = useState(false);
  const [effectSettings, setEffectSettings] = useState({
    halftone: {
      intensity: 1.0,
      gridSize: 15,
      smoothing: 0.15
    },
    hatchBlur: {
      width: 0.1,
      angle: 0.125,
      quality: 16,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.4,
      transitionFeather: 0.25,
      transitionInvert: true
    },
    zoomBlur: {
      strength: 0.25,
      centerX: 0.5,
      centerY: 0.5,
      transitionEnabled: false,
      transitionRadius: 0.5,
      transitionFeather: 0.2,
      transitionInvert: true
    },
    traceEdges: {
      intensity: 4.2,
      threshold: 0.0,
      sobel: true,
      opaque: true,
      fade: 0.01
    },
    ditherBlur: {
      amount: 0.1,
      maxRadius: 0.1,
      jitter: false,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.7,
      transitionFeather: 0.1,
      transitionInvert: true
    },
    gaussianBlur: {
      radius: 15,
      intensity: 1.0,
      quality: 2,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.3,
      transitionFeather: 0.3,
      transitionInvert: true
    },
    ditherBayer: {
      colorize: 0.0,
      matrixMode: 2,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.5,
      transitionFeather: 0.15,
      transitionInvert: true
    },
    dawnBringer: {
      pixelSize: 4.0,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.35,
      transitionFeather: 0.2,
      transitionInvert: true
    },
    pixelArtPalette: {
      pixelScale: 2.0,
      ditherSpread: 0.166,
      // Defaults de transição circular
      transitionEnabled: false,
      transitionRadius: 0.45,
      transitionFeather: 0.2,
      transitionInvert: true
    },
    rainDrops: {
      rainAmount: 0.7,
      blurAmount: 1.0,
      zoom: 1.0,
      timeOffset: 5.0
    },
    sketch: {
      intensity: 1.0,
      angleNum: 3,
      lineWidth: 1.0,
      timeOffset: 0.0
    },
    bwSketch: {
      distortAmount: 1.0,
      edgeIntensity: 1.0,
      distortAll: false,
      invertColor: true,
      timeOffset: 0.0
    },
    sketchBook: {
      intensity: 1.0,
      angleNum: 3,
      lineWidth: 1.0,
      paperTexture: 1.0,
      drawingOpacity: 1.0,
      vignetteStrength: 1.0,
      timeOffset: 0.0
    },
    bwSketchNew: {
      distortAmount: 1.0,
      edgeIntensity: 1.0,
      distortAll: false,
      invertColor: true,
      timeOffset: 0.0
    },
    drawingPractice2: {
      crosshatchIntensity: 1.0,
      edgeThreshold: 1.0,
      strokeWidth: 0.5,
      strokeDensity: 4.0,
      crosshatchModel: 2,
      timeOffset: 0.0
    },
    crossHatchNatron: {
      density: 9.0,
      width: 5.0,
      hatch1: 0.8,
      hatch2: 0.6,
      hatch3: 0.3,
      hatch4: 0.15,
      hatch1Brightness: 0.8,
      hatch2Brightness: 0.6,
      hatch3Brightness: 0.3,
      hatch4Brightness: 0.0,
      edgeIntensity: 1.0
    },
    oilPaintBrush: {
      paintSpec: 0.15,
      vignette: 1.5,
      lightIntensity: 0.9,
      specularPower: 12.0,
      specularIntensity: 1.0,
      shadowIntensity: 0.1
    },
    painted: {
      brushSize: 5.0,
      contrast: 1.2,
      depthPerChannel: 64.0,
      timeOffset: 0.0,
      effectMode: 0
    },
    lensFlare: {
      intensity: 0.5,
      flareSize: 1.0,
      brightness: 0.1,
      mouseX: 0.5,
      mouseY: 0.5,
      time: 0
    },
    lensFlareOnly: {
      intensity: 1.5,
      mouseX: 0.5,
      mouseY: 0.5,
      colorFactor: 0.5,
      colorMix: 0.1,
      time: 0
    },
    anamorphicFlare: {
      intensity: 0.5,
      stretch: 400.0,
      brightness: 0.9,
      mouseX: 0.5,
      mouseY: 0.5
    },
    magicParticles: {
      intensity: 1.0,
      nbParticles: 50,
      motionBlur: 0.73,
      time: 0
    },
    adjustments: {
      brightness: 0.0,
      contrast: 0.0,
      saturation: 0.0,
      sharpness: 0.0,
      sharpnessRadius: 1.0,
      temperature: 0.0,
      tint: 0.0,
      highlights: 0.0,
      shadows: 0.0,
      exposure: 0.0,
      redShadows: 0.0,
      redHighlights: 0.0,
      greenShadows: 0.0,
      greenHighlights: 0.0,
      blueShadows: 0.0,
      blueHighlights: 0.0
    }
  });
  const [activeEffectType, setActiveEffectType] = useState(null); // null = seleção, 'halftone', 'hatchBlur', 'zoomBlur', 'traceEdges', 'ditherBlur', 'rainDrops', 'sketch', 'bwSketch', 'sketchBook', 'bwSketchNew', 'drawingPractice2', 'crossHatchNatron', 'oilPaintBrush', 'painted', 'lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'magicParticles' ou 'adjustments'
  
  // Estado genérico para tabs de efeitos (novo sistema reutilizável)
  const [effectActiveTab, setEffectActiveTab] = useState({
    zoomBlur: 'effect',
    gaussianBlur: 'effect',
    ditherBlur: 'effect',
    hatchBlur: 'effect',
    ditherBayer: 'effect',
    dawnBringer: 'effect',
    pixelArtPalette: 'effect'
  });
  
  // ⚠️ DEPRECATED: Mantido por compatibilidade temporária - usar effectActiveTab ao invés
  const [zoomBlurActiveTab, setZoomBlurActiveTab] = useState('effect'); // 'effect' ou 'transition' - controla visualização no canvas
  
  const [getEffectCanvasCallback, setGetEffectCanvasCallback] = useState(null);
  const [activeEffectCanvas, setActiveEffectCanvas] = useState(null); // Canvas do efeito ativo para preview
  const [effectUpdateCounter, setEffectUpdateCounter] = useState(0); // Contador para forçar re-render

  // Verificar disponibilidade do backend ao montar
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const result = await backendService.checkHealth();
        setBackendAvailable(result.available);

        if (result.available) {
          console.log('Backend disponível:', result.data);
        } else {
          console.log('Backend offline - modo navegador disponível');
        }
      } catch (error) {
        console.warn('Backend check failed:', error);
        setBackendAvailable(false);
      }
    };

    checkBackend();
  }, []);

  // Função para remover fundo usando backend (rápido e preciso)
  const removeBackgroundBackend = useCallback(async (imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }

    // Verificar se backend está disponível
    if (!backendAvailable) {
      console.warn('Backend não disponível');
      alert('Backend offline. Não é possível remover fundo sem o backend.');
      return;
    }

    setBackgroundRemovalLoading(true);

    try {
      console.log('🚀 Iniciando remoção de fundo com backend...');

      // Obter o src da imagem (dentro do objeto HTMLImageElement)
      const imageUrl = image.image?.src || image.src || image.originalSrc;

      if (!imageUrl) {
        throw new Error('URL da imagem não encontrada');
      }

      // Converter data URL para Blob corretamente
      let blob;
      if (imageUrl.startsWith('data:')) {
        // É uma data URL - converter para blob
        const response = await fetch(imageUrl);
        blob = await response.blob();
      } else {
        // É uma URL normal
        const response = await fetch(imageUrl);
        blob = await response.blob();
      }

      // Determinar o tipo MIME correto
      const mimeType = blob.type || 'image/png';
      const extension = mimeType.split('/')[1] || 'png';

      // Criar File com o tipo correto
      const file = new File([blob], `image.${extension}`, { type: mimeType });

      console.log(`📤 Enviando imagem: ${file.name} (${file.type}, ${(file.size / 1024).toFixed(2)} KB)`);

      // Processar no backend
      const result = await backendService.removeBackground(file, {
        model: 'u2net',
        alphaMatting: false,
      });

      // Converter resultado para URL
      const resultUrl = await backendService.blobToDataUrl(result.blob);

      // Criar novo HTMLImageElement com o resultado
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        newImg.onload = resolve;
        newImg.onerror = reject;
        newImg.src = resultUrl;
      });

      console.log(`Fundo removido em ${result.duration}s com modelo ${result.model}`);

      // Atualizar imagem com novo HTMLImageElement
      updateImage(imageId, {
        image: newImg,  // Atualizar o HTMLImageElement
        src: resultUrl,
        backgroundRemoved: true,
        processingTime: result.duration,
        processingModel: result.model,
        processingMethod: 'backend',
      });

      // Salvar no histórico
      saveHistoryState();

    } catch (error) {
      console.error('Erro ao remover fundo com backend:', error);
      alert('Erro ao processar imagem no backend. Verifique se o backend está rodando.');
    } finally {
      setBackgroundRemovalLoading(false);
    }
  }, [state.uploadedImages, backendAvailable, setBackgroundRemovalLoading, updateImage, saveHistoryState]);

  // 🗑️ Funções para modal de confirmação de exclusão
  const openDeleteConfirmation = useCallback((imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }
    setImageToDelete(image);
    setIsDeleteModalOpen(true);
  }, [state.uploadedImages]);

  const cancelDeleteConfirmation = useCallback(() => {
    setIsDeleteModalOpen(false);
    setImageToDelete(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (imageToDelete) {
      removeImage(imageToDelete.id);
      setIsDeleteModalOpen(false);
      setImageToDelete(null);
    }
  }, [imageToDelete, removeImage]);

  // Função para abrir editor avançado
  const openAdvancedEditor = useCallback(async (imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }

    console.log('Abrindo editor avançado para imagem:', imageId);

    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.image?.width || 800;
      canvas.height = image.image?.height || 600;
      const ctx = canvas.getContext('2d');

      let maskData;

      // Se a imagem já tem background removido (tem canal alpha), extrai a máscara do canal alpha
      if (image.backgroundRemoved && image.image) {
        console.log('📸 Extraindo máscara do canal alpha da imagem processada...');

        // Desenha a imagem processada (com transparência) no canvas
        ctx.drawImage(image.image, 0, 0);
        const processedImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Cria máscara baseada no canal alpha
        maskData = ctx.createImageData(canvas.width, canvas.height);
        for (let i = 0; i < processedImageData.data.length; i += 4) {
          const alpha = processedImageData.data[i + 3]; // Canal alpha da imagem processada

          // Máscara em grayscale: 255 = mantido, 0 = removido
          maskData.data[i] = alpha;     // R
          maskData.data[i + 1] = alpha; // G
          maskData.data[i + 2] = alpha; // B
          maskData.data[i + 3] = 255;   // A (sempre opaco)
        }
      } else {
        console.log('📸 Criando máscara branca (imagem sem processamento)...');

        // Imagem ainda não processada - cria máscara toda branca (tudo visível)
        maskData = ctx.createImageData(canvas.width, canvas.height);
        for (let i = 0; i < maskData.data.length; i += 4) {
          maskData.data[i] = 255;     // R
          maskData.data[i + 1] = 255; // G
          maskData.data[i + 2] = 255; // B
          maskData.data[i + 3] = 255; // A
        }
      }

      setAdvancedEditingImage(image);
      setAdvancedEditingMask(maskData);
      setIsAdvancedModalOpen(true);

    } catch (error) {
      console.error('Erro ao abrir editor avançado:', error);
      alert('Erro ao abrir editor avançado. Tente novamente.');
    }
  }, [state.uploadedImages]);

  // Função para aplicar edição avançada
  const applyAdvancedEdit = useCallback(async (resultUrl, mask) => {
    if (!advancedEditingImage || !resultUrl) {
      console.error('Dados inválidos para aplicar edição');
      return;
    }

    try {
      console.log('Aplicando edição avançada...');

      // O modal já retorna a imagem processada com a máscara aplicada
      // Criar novo HTMLImageElement com a imagem processada
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        newImg.onload = resolve;
        newImg.onerror = reject;
        newImg.src = resultUrl;
      });

      // Atualizar imagem com novo HTMLImageElement e src
      updateImage(advancedEditingImage.id, {
        image: newImg,
        src: resultUrl,
        mask: mask,
        lastEdited: new Date(),
        backgroundRemoved: true,
      });

      console.log('Edição aplicada com sucesso');

      // Adicionar ao histórico
      saveHistoryState();

      // Fechar modal
      setIsAdvancedModalOpen(false);
      setAdvancedEditingImage(null);
      setAdvancedEditingMask(null);

    } catch (error) {
      console.error('Erro ao aplicar edição:', error);
      alert('Erro ao aplicar edição. Tente novamente.');
    }
  }, [advancedEditingImage, updateImage, saveHistoryState]);

  // Função para fechar modal sem aplicar
  const closeAdvancedEditor = useCallback(() => {
    setIsAdvancedModalOpen(false);
    setAdvancedEditingImage(null);
    setAdvancedEditingMask(null);
  }, []);

  // Função para finalizar o recorte (aplicar máscara permanentemente)
  const finalizeClipping = useCallback(async (imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);

    if (!image || !image.backgroundRemoved) {
      console.error('Imagem não encontrada ou sem recorte:', imageId);
      return;
    }

    try {
      console.log('Finalizando recorte...');

      // A imagem já tem transparência no canal alpha
      // Vamos apenas remover a máscara para aplicar permanentemente
      updateImage(imageId, {
        mask: null, // Remove a máscara
        clippingFinalized: true, // Marca que o recorte foi finalizado
        lastEdited: new Date(),
      });

      console.log('Recorte finalizado com sucesso');

      // Adicionar ao histórico
      saveHistoryState();

    } catch (error) {
      console.error('Erro ao finalizar recorte:', error);
      alert('Erro ao finalizar recorte. Tente novamente.');
    }
  }, [state.uploadedImages, updateImage, saveHistoryState]);

  // Funções para painel de efeitos
  const openEffectsPanel = useCallback((imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }

    console.log('Abrindo painel de efeitos para imagem:', imageId);
    setEffectsEditingImage(image);
    setIsEffectsPanelOpen(true);
  }, [state.uploadedImages]);

  const closeEffectsPanel = useCallback(() => {
    setIsEffectsPanelOpen(false);
    setEffectsEditingImage(null);
    setActiveEffectType(null); // Reset para seleção
  }, []);

  // Helper para atualizar settings de um efeito específico
  const updateEffectSettings = useCallback((effectType, newSettings) => {
    setEffectSettings(prev => ({
      ...prev,
      [effectType]: {
        ...prev[effectType],
        ...newSettings
      }
    }));
  }, []);

  // Helper para resetar settings de um efeito específico para os valores padrão
  const resetEffectSettings = useCallback((effectType) => {
    const defaultSettings = {
      halftone: {
        intensity: 1.0,
        gridSize: 15,
        smoothing: 0.15
      },
      hatchBlur: {
        width: 0.1,
        angle: 0.125,
        quality: 16,
        transitionEnabled: false,
        transitionRadius: 0.4,
        transitionFeather: 0.25,
        transitionInvert: true
      },
      zoomBlur: {
        strength: 0.25,
        centerX: 0.5,
        centerY: 0.5,
        transitionEnabled: false,
        transitionRadius: 0.5,
        transitionFeather: 0.2,
        transitionInvert: true
      },
      traceEdges: {
        intensity: 4.2,
        threshold: 0.0,
        sobel: true,
        opaque: true,
        fade: 0.01
      },
      ditherBlur: {
        amount: 0.1,
        maxRadius: 0.1,
        jitter: false,
        transitionEnabled: false,
        transitionRadius: 0.7,
        transitionFeather: 0.1,
        transitionInvert: true
      },
      gaussianBlur: {
        radius: 15,
        intensity: 1.0,
        quality: 2,
        transitionEnabled: false,
        transitionRadius: 0.3,
        transitionFeather: 0.3,
        transitionInvert: true
      },
      ditherBayer: {
        colorize: 0.0,
        matrixMode: 2,
        transitionEnabled: false,
        transitionRadius: 0.5,
        transitionFeather: 0.15,
        transitionInvert: true
      },
      dawnBringer: {
        pixelSize: 4.0,
        transitionEnabled: false,
        transitionRadius: 0.35,
        transitionFeather: 0.2,
        transitionInvert: true
      },
      pixelArtPalette: {
        pixelScale: 2.0,
        ditherSpread: 0.166,
        transitionEnabled: false,
        transitionRadius: 0.45,
        transitionFeather: 0.2,
        transitionInvert: true
      },
      rainDrops: {
        rainAmount: 0.7,
        blurAmount: 1.0,
        zoom: 1.0,
        timeOffset: 5.0
      },
      sketch: {
        intensity: 1.0,
        angleNum: 3,
        lineWidth: 1.0,
        transitionEnabled: false,
        transitionRadius: 0.4,
        transitionFeather: 0.2,
        transitionInvert: true
      },
      adjustments: {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        sharpness: 0,
        temperature: 0,
        tint: 0,
        highlights: 0,
        shadows: 0,
        exposure: 0,
        redShadows: 0,
        redHighlights: 0,
        greenShadows: 0,
        greenHighlights: 0,
        blueShadows: 0,
        blueHighlights: 0
      }
    };

    if (defaultSettings[effectType]) {
      setEffectSettings(prev => ({
        ...prev,
        [effectType]: defaultSettings[effectType]
      }));
    }
  }, []);

  const applyEffect = useCallback(async (resultUrlOrCanvas) => {
    if (!effectsEditingImage) {
      console.error('Nenhuma imagem sendo editada');
      return;
    }

    try {
      console.log('[applyEffect] Aplicando efeito...');

      let resultUrl = resultUrlOrCanvas;

      // Se nenhum parâmetro foi passado, tentar pegar do callback
      if (!resultUrl && getEffectCanvasCallback) {
        resultUrl = getEffectCanvasCallback();
      }

      // Se for um canvas, converter para dataURL
      if (resultUrl && typeof resultUrl === 'object' && resultUrl.toDataURL) {
        console.log('[applyEffect] Convertendo canvas para data URL...');
        resultUrl = resultUrl.toDataURL('image/png');
      }

      if (!resultUrl) {
        console.error('[applyEffect] Dados inválidos para aplicar efeito');
        return;
      }

      console.log('🖼️ [applyEffect] Criando novo HTMLImageElement...');
      // Criar novo HTMLImageElement com resultado
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        newImg.onload = () => {
          console.log('[applyEffect] Imagem carregada:', {
            width: newImg.naturalWidth,
            height: newImg.naturalHeight,
            complete: newImg.complete
          });
          resolve();
        };
        newImg.onerror = (error) => {
          console.error('[applyEffect] Erro ao carregar imagem:', error);
          reject(error);
        };
        newImg.src = resultUrl;
      });

      // IMPORTANTE: Verificar se a imagem realmente carregou
      if (!newImg.complete || newImg.naturalWidth === 0) {
        console.error('[applyEffect] Imagem não carregou corretamente:', {
          complete: newImg.complete,
          naturalWidth: newImg.naturalWidth,
          naturalHeight: newImg.naturalHeight
        });
        throw new Error('Falha ao carregar imagem processada');
      }

      console.log('💾 [applyEffect] Atualizando imagem no estado...');
      // Atualizar imagem
      updateImage(effectsEditingImage.id, {
        image: newImg,
        src: resultUrl,
        lastEdited: new Date(),
        hasEffect: true,
      });

      console.log('[applyEffect] Efeito aplicado com sucesso!');

      // LOG: Registrar aplicação do efeito
      if (window.__logAction) {
        window.__logAction('APPLY_EFFECT', effectsEditingImage.id, {
          effectType: activeEffectType,
          settings: effectSettings[activeEffectType] || {}
        });
      }

      // Adicionar ao histórico
      saveHistoryState();

      // Fechar painel
      closeEffectsPanel();

    } catch (error) {
      console.error('❌ Erro ao aplicar efeito:', error);
      alert('Erro ao aplicar efeito. Tente novamente.');
    }
  }, [effectsEditingImage, updateImage, saveHistoryState, closeEffectsPanel, getEffectCanvasCallback, activeEffectType, effectSettings]);

  // 🎨 Funções para painel de filtros de cores
  const openColorFiltersPanel = useCallback((imageId) => {
    const image = state.uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('Imagem não encontrada:', imageId);
      return;
    }

    console.log('🎨 Abrindo painel de filtros de cores para imagem:', imageId);
    setColorFilterEditingImage(image);
    setIsColorFiltersPanelOpen(true);
  }, [state.uploadedImages]);

  const closeColorFiltersPanel = useCallback(() => {
    setIsColorFiltersPanelOpen(false);
    setColorFilterEditingImage(null);
    setActiveColorFilterType(null); // Reset para seleção
  }, []);

  // Helper para atualizar settings de um filtro específico
  const updateColorFilterSettings = useCallback((filterType, newSettings) => {
    setColorFilterSettings(prev => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        ...newSettings
      }
    }));
  }, []);

  const applyColorFilter = useCallback(async (resultUrlOrCanvas) => {
    if (!colorFilterEditingImage) {
      console.error('Nenhuma imagem sendo editada');
      return;
    }

    try {
      console.log('✅ Aplicando filtro de cor...');

      let colorFilterCanvas = resultUrlOrCanvas;

      // Se nenhum parâmetro foi passado, tentar pegar do callback
      if (!colorFilterCanvas && getColorFilterCanvasCallback) {
        colorFilterCanvas = getColorFilterCanvasCallback();
      }

      if (!colorFilterCanvas) {
        console.error('Dados inválidos para aplicar filtro de cor');
        return;
      }

      // Criar canvas para combinar imagem original + filtro de cor
      const combinedCanvas = document.createElement('canvas');
      const originalImage = colorFilterEditingImage.image;
      
      combinedCanvas.width = originalImage.width;
      combinedCanvas.height = originalImage.height;
      const ctx = combinedCanvas.getContext('2d');

      // 1️⃣ Desenhar imagem original
      ctx.drawImage(originalImage, 0, 0);

      // 2️⃣ Desenhar filtro de cor por cima com blend mode
      const filterSettings = colorFilterSettings[activeColorFilterType];
      const filterOpacity = Math.max(1, filterSettings?.opacity || 100);
      const blendMode = filterSettings?.blendMode || 'normal';

      console.log('🔍 Debug applyColorFilter:', {
        colorFilterCanvas,
        colorFilterCanvasType: typeof colorFilterCanvas,
        isCanvas: colorFilterCanvas instanceof HTMLCanvasElement,
        filterSettings,
        filterOpacity,
        blendMode
      });

      // Converter data URL para imagem se necessário
      let colorFilterImage = colorFilterCanvas;
      
      if (typeof colorFilterCanvas === 'string' && colorFilterCanvas.startsWith('data:')) {
        console.log('🔄 Convertendo data URL para imagem...');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            console.log('✅ Data URL convertida para imagem');
            resolve();
          };
          img.onerror = reject;
          img.src = colorFilterCanvas;
        });
        
        colorFilterImage = img;
      } else if (!(colorFilterCanvas instanceof HTMLCanvasElement)) {
        console.error('❌ Canvas de filtro de cor inválido:', colorFilterCanvas);
        throw new Error('Canvas de filtro de cor inválido');
      }

      ctx.save();
      ctx.globalAlpha = filterOpacity / 100;
      ctx.globalCompositeOperation = blendMode;
      
      // WebGL tem coordenadas Y invertidas
      ctx.translate(0, combinedCanvas.height);
      ctx.scale(1, -1);
      ctx.drawImage(colorFilterImage, 0, 0, combinedCanvas.width, combinedCanvas.height);
      ctx.restore();

      // Converter para data URL
      console.log('🔄 [applyColorFilter] Convertendo canvas combinado para data URL...');
      const resultUrl = combinedCanvas.toDataURL('image/png');

      // Criar novo HTMLImageElement com resultado
      console.log('🖼️ [applyColorFilter] Criando novo HTMLImageElement...');
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        newImg.onload = () => {
          console.log('✅ [applyColorFilter] Imagem carregada:', {
            width: newImg.naturalWidth,
            height: newImg.naturalHeight,
            complete: newImg.complete
          });
          resolve();
        };
        newImg.onerror = (error) => {
          console.error('❌ [applyColorFilter] Erro ao carregar imagem:', error);
          reject(error);
        };
        newImg.src = resultUrl;
      });

      // IMPORTANTE: Verificar se a imagem realmente carregou
      if (!newImg.complete || newImg.naturalWidth === 0) {
        console.error('❌ [applyColorFilter] Imagem não carregou corretamente:', {
          complete: newImg.complete,
          naturalWidth: newImg.naturalWidth,
          naturalHeight: newImg.naturalHeight
        });
        throw new Error('Falha ao carregar imagem processada');
      }

      console.log('💾 [applyColorFilter] Atualizando imagem no estado...');
      // Atualizar imagem
      updateImage(colorFilterEditingImage.id, {
        image: newImg,
        src: resultUrl,
        lastEdited: new Date(),
        hasColorFilter: true,
      });

      console.log('✅ [applyColorFilter] Filtro de cor aplicado com sucesso!');

      // 📋 LOG: Registrar aplicação do filtro de cor
      if (window.__logAction) {
        window.__logAction('APPLY_COLOR_FILTER', colorFilterEditingImage.id, {
          filterType: activeColorFilterType,
          settings: colorFilterSettings[activeColorFilterType] || {}
        });
      }

      // Adicionar ao histórico
      saveHistoryState();

      // Fechar painel
      closeColorFiltersPanel();

    } catch (error) {
      console.error('❌ Erro ao aplicar filtro de cor:', error);
      alert('Erro ao aplicar filtro de cor. Tente novamente.');
    }
  }, [colorFilterEditingImage, updateImage, saveHistoryState, closeColorFiltersPanel, getColorFilterCanvasCallback, colorFilterSettings, activeColorFilterType]);

  // 🎨 Funções para pintura
  const applyPainting = useCallback(async (resultImage) => {
    if (!state.selectedImageId || !resultImage) {
      console.error('Dados inválidos para aplicar pintura');
      return;
    }

    try {
      console.log('✅ Aplicando pintura...');

      // Criar data URL a partir da imagem
      const canvas = document.createElement('canvas');
      canvas.width = resultImage.width;
      canvas.height = resultImage.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(resultImage, 0, 0);
      const resultUrl = canvas.toDataURL('image/png');

      // Atualizar imagem com o resultado da pintura
      updateImage(state.selectedImageId, {
        image: resultImage,
        src: resultUrl,
        lastEdited: new Date(),
        hasPainting: true,
      });

      console.log('✅ Pintura aplicada com sucesso');

      // Adicionar ao histórico
      saveHistoryState();

      // Desativar modo de pintura
      setIsPaintingActive(false);

    } catch (error) {
      console.error('❌ Erro ao aplicar pintura:', error);
      alert('Erro ao aplicar pintura. Tente novamente.');
    }
  }, [state.selectedImageId, updateImage, saveHistoryState]);

  // Verificar se pode fazer undo/redo
  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  // 🔍 Expor função de debug globalmente (apenas em desenvolvimento)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugLayers = () => debugLayers(state.uploadedImages);
    }
  }, [state.uploadedImages]);

  // 🎯 Funções helper para gerenciar tabs de efeitos (sistema genérico)
  const setEffectTab = useCallback((effectType, tab) => {
    setEffectActiveTab(prev => ({
      ...prev,
      [effectType]: tab
    }));
    
    // 🔄 Sincronizar com estado deprecated para compatibilidade
    if (effectType === 'zoomBlur') {
      setZoomBlurActiveTab(tab);
    }
  }, []);

  const getEffectTab = useCallback((effectType) => {
    return effectActiveTab[effectType] || 'effect';
  }, [effectActiveTab]);

  // Valor do contexto
  const contextValue = {
    // Estado
    ...state,
    
    
    // Ações de canvas
    clearCanvas,
    saveCanvasState,
    
    // Ações de imagens
    addImage,
    removeImage,
    removeAllImages,
    updateImageTransform,
    applyImageTransform, // 🆕 Novo: Aplicar transformações pré-definidas
    setSelectedImage,
    updateImageOpacity,
    updateImageBlendMode,
    reorderImages,
    updateImage,
    duplicateImage,
    
    // Ações de histórico
    saveHistoryState,
    undo,
    redo,
    canUndo,
    canRedo,
    setMobileLayerDrawerOpen,
    setMobileLayerDrawerHeight,
    setBackgroundRemovalLoading,

    // 🆕 Backend e modal avançado
    backendAvailable,
    removeBackgroundBackend,
    isAdvancedModalOpen,
    advancedEditingImage,
    advancedEditingMask,
    openAdvancedEditor,
    applyAdvancedEdit,
    closeAdvancedEditor,
    setIsAdvancedModalOpen,
    finalizeClipping,

    // 🗑️ Modal de confirmação de exclusão
    isDeleteModalOpen,
    imageToDelete,
    openDeleteConfirmation,
    cancelDeleteConfirmation,
    confirmDelete,

    // 🎨 Efeitos
    isEffectsPanelOpen,
    effectsEditingImage,
    effectSettings,
    setEffectSettings,
    updateEffectSettings,
    resetEffectSettings,
    activeEffectType,
    setActiveEffectType,
    
    // 🎯 Sistema genérico de tabs (novo)
    effectActiveTab,
    setEffectTab,
    getEffectTab,
    
    // ⚠️ DEPRECATED: Usar effectActiveTab/setEffectTab ao invés
    zoomBlurActiveTab,
    setZoomBlurActiveTab,
    
    openEffectsPanel,
    closeEffectsPanel,
    applyEffect,
    setGetEffectCanvasCallback,
    activeEffectCanvas,
    setActiveEffectCanvas,
    effectUpdateCounter,
    setEffectUpdateCounter,
    
    // 🎨 Filtros de Cor (NOVO SISTEMA)
    isColorFiltersPanelOpen,
    colorFilterEditingImage,
    activeColorFilterType,
    setActiveColorFilterType,
    colorFilterSettings,
    setColorFilterSettings,
    updateColorFilterSettings,
    openColorFiltersPanel,
    closeColorFiltersPanel,
    applyColorFilter,
    setGetColorFilterCanvasCallback,
    colorFilterUpdateCounter,
    setColorFilterUpdateCounter,
    
    // 🎨 Filtros de Cor (LEGACY - compatibilidade temporária)
    selectedColorFilter,
    setSelectedColorFilter,
    colorBlendMode,
    setColorBlendMode,
    colorOpacity,
    setColorOpacity,
    activeColorFilterCanvas,
    setActiveColorFilterCanvas,
    
    // 🎨 Pintura
    isPaintingActive,
    setIsPaintingActive,
    isSimplePaintingMode, // ✨ NOVO: Modo pintura simplificado
    setIsSimplePaintingMode, // ✨ NOVO: Modo pintura simplificado
    isPainting2Mode, // 🎨 NOVO: Modo Pintura2
    setIsPainting2Mode, // 🎨 NOVO: Modo Pintura2
    paintingSettings,
    setPaintingSettings,
    applyPainting,
    
    // 📄 Papel
    isPaperPanelOpen,
    setIsPaperPanelOpen,
    
    // 📊 Análise de Estado
    isStateAnalyzerOpen,
    setIsStateAnalyzerOpen,
  };


  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp deve ser usado dentro de um AppProvider');
  }
  return context;
};

export default AppContext;
